import type { CSSProperties } from 'react';
import type {
  CourseMetadataTranslationProjection,
  TranslationRecordStatus,
} from '@course-authoring/shared-types';
import {
  TranslationReplacementPrompt,
  TranslationStatusBadge,
  TranslationStatusNote,
  type TranslationReplacementView,
} from '../../../components/TranslationStatusPrimitives';
import { CourseTagPicker } from '../../../components/tagPicker/CourseTagPicker';
import type { CourseDraft } from '../types';
import type { CourseMetadataDraft } from './useCourseMetadataAutosave';

interface CourseMetadataSectionProps {
  draft: CourseDraft;
  metadata: CourseMetadataDraft;
  mode: 'english' | 'translation';
  translationStatus?: TranslationRecordStatus;
  editabilityReason: CourseMetadataTranslationProjection['editabilityReason'];
  humanTranslationCount: number;
  replacementView: TranslationReplacementView | null;
  isSubmitting: boolean;
  disabled: boolean;
  styles: Record<string, CSSProperties>;
  onUpdateDraft: (updates: Partial<CourseDraft>) => void;
  onUpdateMetadata: (updates: Partial<CourseMetadataDraft>) => void;
  onQueueTranslations: () => void;
  onPreserveTranslations: () => void;
  onChangeReplacementDecision: () => void;
  onDurationChange: (value: string) => void;
  onMinimumRequiredChange: (value: string) => void;
  onOpenImageModal: () => void;
}

export function CourseMetadataSection({
  draft,
  metadata,
  mode,
  translationStatus,
  editabilityReason,
  humanTranslationCount,
  replacementView,
  isSubmitting,
  disabled,
  styles,
  onUpdateDraft,
  onUpdateMetadata,
  onQueueTranslations,
  onPreserveTranslations,
  onChangeReplacementDecision,
  onDurationChange,
  onMinimumRequiredChange,
  onOpenImageModal,
}: CourseMetadataSectionProps) {
  const isTranslation = mode === 'translation';
  const waitsForPublication = draft.workflowState !== 'published';
  const pendingTranslationWaitsForPublication = waitsForPublication
    && (translationStatus === 'ht-update-pending' || translationStatus === 'mt-update-pending');
  const pendingTranslationMessage = translationStatus === 'ht-update-pending'
    ? 'The English source was updated. This human translation will be replaced with a machine translation after this course is published and a scheduled translation run processes it.'
    : 'The English source was updated. This machine translation will be replaced automatically after this course is published and a scheduled translation run processes it.';
  const schedulingNoticeStyle: CSSProperties = {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #f0c040',
    backgroundColor: '#fffbf0',
    color: '#7a4e00',
    fontSize: '13px',
    lineHeight: 1.4,
  };
  return (
    <section style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>Course metadata</h2>
        {isTranslation && translationStatus && (
          <TranslationStatusBadge status={translationStatus} />
        )}
      </div>
      <div style={styles.cardBody}>
        {!isTranslation && replacementView && (
          <>
            <TranslationReplacementPrompt
              humanTranslationCount={humanTranslationCount}
              view={replacementView}
              disabled={isSubmitting}
              onQueue={onQueueTranslations}
              onPreserve={onPreserveTranslations}
              onChange={onChangeReplacementDecision}
            />
            {waitsForPublication && replacementView !== 'preserved' && (
              <div role="note" style={schedulingNoticeStyle}>
                {replacementView === 'queued'
                  ? 'This course is not published. Queued replacements will wait until the course is published and a scheduled translation run processes them.'
                  : 'This course is not published. If you choose Yes, replacements will wait until the course is published and a scheduled translation run processes them.'}
              </div>
            )}
          </>
        )}
        {isTranslation && translationStatus && (
          pendingTranslationWaitsForPublication
            ? <div role="note" style={schedulingNoticeStyle}>{pendingTranslationMessage}</div>
            : <TranslationStatusNote status={translationStatus} />
        )}
        {isTranslation && editabilityReason === 'missing-client-english-source' && (
          <div
            role="note"
            style={schedulingNoticeStyle}
          >
            Customize this client&apos;s English course metadata before editing its translations.
          </div>
        )}
        <label style={styles.field}>
          <span style={styles.label}>Title</span>
          <input
            type="text"
            value={metadata.title}
            onChange={(event) => onUpdateMetadata({ title: event.target.value })}
            style={styles.input}
            disabled={disabled}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.label}>Description</span>
          <textarea
            value={metadata.description}
            onChange={(event) => onUpdateMetadata({ description: event.target.value })}
            style={styles.textarea}
            disabled={disabled}
          />
        </label>
        {!isTranslation && <CourseTagPicker courseId={draft.id} disabled={disabled} />}
        {!isTranslation && <div style={styles.inlineRow}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={draft.timedCourse}
              onChange={(event) => onUpdateDraft({ timedCourse: event.target.checked })}
            />
            Timed course
          </label>
          <label style={styles.field}>
            <span style={styles.label}>
              {draft.timedCourse ? 'Min. required (min)' : 'Duration (min)'}
            </span>
            <input
              type="number"
              min={1}
              value={draft.timedCourse
                ? draft.minimumRequiredMinutes ?? ''
                : draft.durationMinutes ?? ''}
              onChange={(event) => {
                if (draft.timedCourse) {
                  onMinimumRequiredChange(event.target.value);
                } else {
                  onDurationChange(event.target.value);
                }
              }}
              style={styles.input}
            />
          </label>
        </div>}
        {!isTranslation && <div style={styles.field}>
          <span style={styles.label}>Thumbnail</span>
          <button
            type="button"
            style={styles.thumbnailButton}
            onClick={onOpenImageModal}
          >
            {draft.thumbnailSrc ? (
              <img
                src={draft.thumbnailSrc}
                alt={draft.thumbnailAlt || 'Course thumbnail'}
                style={styles.thumbnailImage}
              />
            ) : (
              'Click to select image'
            )}
          </button>
        </div>}
        {!isTranslation && <div style={styles.toggleRow}>
          <div>
            <div style={styles.label}>Enable completion certificate</div>
            <div style={styles.mutedText}>
              Learners can download a personalized completion certificate from the course
              completion screen.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={draft.completionCertificateEnabled}
            onClick={() => onUpdateDraft({
              completionCertificateEnabled: !draft.completionCertificateEnabled,
            })}
            style={{
              ...styles.toggleButton,
              ...(draft.completionCertificateEnabled ? {} : styles.toggleButtonOff),
            }}
          >
            <span style={styles.toggleKnob} />
          </button>
        </div>}
        {(isTranslation || draft.completionCertificateEnabled) && (
          <label style={styles.field}>
            <span style={styles.label}>{isTranslation ? 'Certificate message' : 'Certificate text'}</span>
            <textarea
              value={metadata.certificateMessage}
              onChange={(event) => onUpdateMetadata({ certificateMessage: event.target.value })}
              style={{ ...styles.textarea, minHeight: '72px' }}
              disabled={disabled}
            />
          </label>
        )}
        <label style={styles.field}>
          <span style={styles.label}>{isTranslation ? 'End-of-program message' : 'End-of-course message'}</span>
          <textarea
            value={metadata.completedMessage}
            onChange={(event) => onUpdateMetadata({ completedMessage: event.target.value })}
            style={{ ...styles.textarea, minHeight: '72px' }}
            disabled={disabled}
          />
        </label>
      </div>
    </section>
  );
}
